import { createAsyncThunk } from "@reduxjs/toolkit";

import * as userAPI from "../../../api/user";
import { ICSRAssistedUser } from "../../../models/user";

/**
 * Load CSR assisted user data.
 *
 * When a Customer Service Representative (CSR) is assisting a customer, this
 * fetches the customer's data. If an email is present, it's automatically
 * populated in the guest email field. Returns null if no assisted user session.
 *
 * @returns {Promise<ICSRAssistedUser | null>} Promise resolving to assisted user data or null
 *
 * @example
 * // Check for assisted user on checkout page mount
 * const user = await dispatch(loadAssistedUser()).unwrap();
 * if (user) {
 *   console.log("CSR assisting:", user.username);
 * }
 *
 * @sideEffects
 * - Updates `data.assisted_user` with fetched user data via extraReducers
 * - Updates `form.guest_email` with user's email if present via extraReducers
 * - Logs to console in debug mode
 */
export const loadAssistedUser = createAsyncThunk(
    "checkout/loadAssistedUser",
    async (): Promise<ICSRAssistedUser | null> => {
        console.debug("Loading CSR's assisted user...");
        const user = await userAPI.getCurrentlyAssistedUser();

        if (!user) {
            console.debug("CSR is not currently assisting a user.");
            return null;
        }

        console.debug(`Loaded CSR's assisted user: ${user.username}.`);
        return user;
    },
);
